import { matchPath } from "react-router-dom";
import { Language } from "@/api/data-contracts";
import { AppPaths } from "@/app/AppPaths";
import { LANGUAGE_TO_LNG } from "@/app/locale/languageCodes";

/**
 * Each language in its own spelling. These are deliberately not UI texts from the database: a visitor looking for
 * their language has to recognise its name whatever the current display language is.
 */
export const VISITOR_LANGUAGE_NAMES: Record<Language, string> = {
    [Language.GERMAN]: "Deutsch",
    [Language.ENGLISH]: "English",
    [Language.POLISH]: "Polski",
    [Language.ITALIAN]: "Italiano",
    [Language.FRENCH]: "Français",
    [Language.CZECH]: "Čeština"
};

export type VisitorFlagCountry = "DE" | "GB" | "PL" | "IT" | "FR" | "CZ";

export type VisitorLanguageFlag = { kind: "flag"; country: VisitorFlagCountry } | { kind: "code"; code: string };

// Partial on purpose: a language added to the generated enum later shows its code until it gets a flag here.
const FLAG_COUNTRIES: Partial<Record<Language, VisitorFlagCountry>> = {
    [Language.GERMAN]: "DE",
    [Language.ENGLISH]: "GB",
    [Language.POLISH]: "PL",
    [Language.ITALIAN]: "IT",
    [Language.FRENCH]: "FR",
    [Language.CZECH]: "CZ"
};

/**
 * `href` with `lang` set to the language code in the query before the `#`, replacing any value there. The
 * hash, including its own query, belongs to the hash route and stays as it is.
 */
export function withVisitorLanguageParam(href: string, language: Language): string {
    const url = new URL(href);
    url.searchParams.set("lang", LANGUAGE_TO_LNG[language]);
    return url.toString();
}

/**
 * Puts the visitor language into the current address without a new history entry, so a reload keeps the visitor's
 * pick (the anonymous language resolution in `AuthContext` reads `lang` first).
 */
export function writeVisitorLanguageToAddress(language: Language): void {
    // The hash router keeps its index and key in history.state; passing null would break its back navigation.
    window.history.replaceState(window.history.state, "", withVisitorLanguageParam(window.location.href, language));
}

/**
 * The languages the visitor can pick, in enum declaration order like the profile selection. German is always
 * offered; an empty list (not loaded or loading failed) offers German and English.
 */
export function visitorLanguageOptions(availableLanguages: Language[]): Language[] {
    const offered =
        availableLanguages.length === 0
            ? [Language.GERMAN, Language.ENGLISH]
            : [...availableLanguages, Language.GERMAN];
    return Object.values(Language).filter((language) => offered.includes(language));
}

/** The flag shown for a language; a language without an assigned flag shows its upper-case language code. */
export function visitorLanguageFlag(language: Language): VisitorLanguageFlag {
    const country = FLAG_COUNTRIES[language];
    return country ? { kind: "flag", country } : { kind: "code", code: LANGUAGE_TO_LNG[language].toUpperCase() };
}

/** The pages whose header offers the visitor language switch; a positive list, so a new page stays without it. */
const VISITOR_LANGUAGE_SWITCH_PATHS = [
    AppPaths.LANDING_PAGE,
    AppPaths.IMPRESSUM_PAGE,
    AppPaths.PRIVACY_PAGE,
    AppPaths.COOKIE_POLICY_PAGE,
    AppPaths.USER_PASSWORD_RESET_REQUEST,
    AppPaths.USER_PASSWORD_RESET
];

/** Returns true when the header shows the visitor language switch: only without sign-in, only on listed pages. */
export function showsVisitorLanguageSwitch(authenticated: boolean, pathname: string): boolean {
    return !authenticated && VISITOR_LANGUAGE_SWITCH_PATHS.some((path) => matchPath(path, pathname) !== null);
}
